import { useState } from "react";
import type { ValueFilter } from "@lucralink/shared";
import { Input } from "@/components/ui/input";
import { formatReaisInput, parseReais } from "@/lib/forwarding";
import { Toggle, selectClassName } from "./controls";

interface ValueFilterFieldsProps {
  readonly idPrefix: string;
  readonly value: ValueFilter;
  readonly onChange: (value: ValueFilter) => void;
}

function MoneyField({
  id,
  label,
  cents,
  onCents,
}: {
  id: string;
  label: string;
  cents: number | null;
  onCents: (c: number | null) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(formatReaisInput(cents));
  const parsed = parseReais(draft);
  const invalid = parsed !== null && Number.isNaN(parsed);
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          R$
        </span>
        <Input
          id={id}
          inputMode="decimal"
          className="pl-9"
          placeholder="sem limite"
          value={draft}
          aria-invalid={invalid}
          onChange={(e) => {
            setDraft(e.target.value);
            const next = parseReais(e.target.value);
            if (next === null || !Number.isNaN(next)) onCents(next);
          }}
        />
      </div>
      {invalid && <p className="text-[11px] text-destructive">Use o formato 199,90</p>}
    </div>
  );
}

interface FieldArgs {
  readonly id: string;
  readonly value: ValueFilter;
  readonly set: (patch: Partial<ValueFilter>) => void;
}

function DiscountField({ id, value, set }: FieldArgs): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        Desconto mínimo (%)
      </label>
      <Input
        id={id}
        type="number"
        min={0}
        max={100}
        placeholder="sem mínimo"
        value={value.minDiscountPercent ?? ""}
        onChange={(e) => {
          const n = Number(e.target.value);
          set({
            minDiscountPercent: e.target.value === "" || n <= 0 ? null : Math.min(100, n),
          });
        }}
      />
    </div>
  );
}

function UnknownPriceField({ id, value, set }: FieldArgs): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        Quando não achar o preço
      </label>
      <select
        id={id}
        className={selectClassName}
        value={value.priceUnknownAction}
        onChange={(e) => set({ priceUnknownAction: e.target.value === "drop" ? "drop" : "pass" })}
      >
        <option value="pass">Encaminhar mesmo assim</option>
        <option value="drop">Descartar a oferta</option>
      </select>
    </div>
  );
}

/** Campos do filtro por valor (padrao global e override por canal). */
export function ValueFilterFields({
  idPrefix,
  value,
  onChange,
}: ValueFilterFieldsProps): JSX.Element {
  const set = (patch: Partial<ValueFilter>): void => onChange({ ...value, ...patch });
  return (
    <div className="space-y-4">
      <Toggle
        id={`${idPrefix}-enabled`}
        checked={value.enabled}
        onChange={(enabled) => set({ enabled })}
        label="Filtrar ofertas por valor"
        hint="Só encaminha ofertas dentro da faixa de preço e com o desconto mínimo."
      />
      {value.enabled && (
        <div className="grid gap-3 sm:grid-cols-2">
          <MoneyField
            id={`${idPrefix}-min`}
            label="Preço mínimo"
            cents={value.minPriceCents}
            onCents={(c) => set({ minPriceCents: c })}
          />
          <MoneyField
            id={`${idPrefix}-max`}
            label="Preço máximo"
            cents={value.maxPriceCents}
            onCents={(c) => set({ maxPriceCents: c })}
          />
          <DiscountField id={`${idPrefix}-discount`} value={value} set={set} />
          <UnknownPriceField id={`${idPrefix}-unknown`} value={value} set={set} />
        </div>
      )}
    </div>
  );
}
